import React, { useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { formatUsd } from '../../utils/agentCost';

/**
 * Ask an agent something directly — "how have the tickets been today?".
 *
 * The conversation is kept open server-side per person per agent, so follow-up
 * questions have context. It is the same agent, with the same briefing, that
 * does the pipeline work: asking it something is a way to check what it knows
 * before trusting it with a task.
 */
export default function AgentChat({ workspaceId, agent, fnNode, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [spent, setSpent]       = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft('');
    setError('');
    setMessages(m => [...m, { who: 'you', text }]);
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'askAgentDirect')({
        workspaceId, functionNodeId: fnNode.id, agentId: agent.id, message: text,
      });
      const d = res?.data || {};
      if (!d.success) {
        setError(d.error || 'That did not work.');
      } else {
        setMessages(m => [...m, { who: 'agent', text: d.reply }]);
        if (d.usdCost !== undefined && d.usdCost !== null) setSpent(d.usdCost);
      }
    } catch (e) {
      setError(e.message || 'Could not reach the agent.');
    }
    setBusy(false);
  };

  return (
    <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box agent-chat-box">
        <div className="modal-header">
          <span className="modal-icon">🤖</span>
          <h3 className="modal-title">{agent.name || 'Agent'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="agent-chat-sub">
          {fnNode.data?.label || 'Team'}{agent.role ? ` · ${agent.role}` : ''}
          {spent !== null && <span className="agent-chat-cost"> · this conversation has cost {formatUsd(spent)}</span>}
        </div>

        <div className="agent-chat-log">
          {messages.length === 0 && !busy && (
            <div className="agent-chat-hint">
              Ask it anything it would know from its briefing and the work it has done —
              “how have the tickets been today?”, “what keeps coming up?”, “what would you
              need to finish that last one?”. It answers with the same knowledge it uses
              when a pipeline gives it a task.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`agent-chat-msg ${m.who}`}>
              <div className="agent-chat-who">{m.who === 'you' ? 'You' : (agent.name || 'Agent')}</div>
              <div className="agent-chat-text">{m.text}</div>
            </div>
          ))}
          {busy && (
            <div className="agent-chat-msg agent">
              <div className="agent-chat-who">{agent.name || 'Agent'}</div>
              <div className="agent-chat-text agent-chat-thinking">thinking…</div>
            </div>
          )}
          {error && <div className="agent-chat-error">{error}</div>}
          <div ref={endRef} />
        </div>

        <div className="agent-chat-input">
          <textarea
            className="panel-textarea"
            rows={2}
            value={draft}
            placeholder={`Ask ${agent.name || 'this agent'} something…`}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
          />
          <button className="panel-save-btn" style={{ width: 'auto', padding: '8px 18px' }}
                  onClick={send} disabled={busy || !draft.trim()}>
            {busy ? 'Asking…' : 'Ask'}
          </button>
        </div>
      </div>
    </div>
  );
}
